// Loads the EntityStore (full sync graph) once the user is signed in and shares
// it through context. A monotonically increasing `rev` lets consumers re-render
// after the initial load and after local writes (renames, new makeovers, ideas).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { EntityStore } from "./entityStore";
import { useAuth } from "./auth";

interface StoreState {
  store: EntityStore;
  rev: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Bump after a local mutation so views recompute selectors. */
  touch: () => void;
}

const StoreCtx = createContext<StoreState | null>(null);

export function useStore(): StoreState {
  const ctx = useContext(StoreCtx);
  if (!ctx) throw new Error("useStore must be used within <StoreProvider>");
  return ctx;
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const storeRef = useRef<EntityStore>(new EntityStore());
  const [rev, setRev] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (status !== "signedIn") return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fresh = new EntityStore();
    fresh
      .load()
      .then(() => {
        if (cancelled) return;
        storeRef.current = fresh;
        setRev((r) => r + 1);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const touch = useCallback(() => setRev((r) => r + 1), []);

  return (
    <StoreCtx.Provider value={{ store: storeRef.current, rev, loading, error, reload, touch }}>
      {children}
    </StoreCtx.Provider>
  );
}
